/**
 * Global call state: invitations, ringing UI and the active media session.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

import { useAuth } from '@/api/auth'
import { subscribeCallEvents } from '@/api/callEvents'
import { declineCall, fetchActiveCall, startCall } from '@/api/calls'
import { useRooms } from '@/api/queries'
import type { Call, UUID } from '@/api/types'
import { CallPill } from '@/components/CallPill'
import { CallWindow, IncomingCallPrompt, type CallStage } from '@/components/CallWindow'
import { roomLabel, useRoomLabels } from '@/components/Sidebar'
import { useCallSession, type CallPreferences } from '@/hooks/useCallSession'
import { startRingtone, type Ringtone } from '@/lib/ringtone'

/**
 * What the call dialog is waiting to do. Nothing is asked of the server until
 * the person has looked at their own camera and pressed Join.
 */
type Pending =
  | { kind: 'start'; roomId: UUID; label: string }
  | { kind: 'join'; call: Call; label: string }

interface CallContextValue {
  callsEnabled: boolean
  incoming: Call | null
  activeCall: Call | null
  /** Opens the dialog on its pre-join step. */
  openRoomCall: (roomId: UUID, label: string) => void
  /** Opens the dialog for a call already under way in the room. */
  joinExistingCall: (call: Call, label: string) => void
  acceptIncoming: () => void
  acceptIncomingAudioOnly: () => void
  declineIncoming: () => Promise<void>
}

const CallContext = createContext<CallContextValue | null>(null)

export function useCalls(): CallContextValue {
  const value = useContext(CallContext)
  if (value === null) throw new Error('useCalls must be used within CallProvider')
  return value
}

function participantStatus(call: Call, userId: UUID | undefined): string | null {
  if (userId === undefined) return null
  return call.participants.find((participant) => participant.user?.id === userId)?.status ?? null
}

function initiatorName(call: Call): string {
  const initiator = call.initiator
  if (initiator === null) return 'Someone'
  const display = initiator.display_name.trim()
  return display === '' ? initiator.username : display
}

/**
 * A system notification for a call that arrives while the tab is not the
 * thing on screen. The browser ringtone does not carry out of a background
 * tab on every platform; this does. Only when permission is already there —
 * a call is the wrong moment to ask for it.
 */
function notifyIncoming(call: Call, onOpen: () => void): (() => void) | null {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return null
  if (typeof document !== 'undefined' && document.visibilityState === 'visible' && document.hasFocus()) {
    return null
  }
  const notification = new Notification(`${initiatorName(call)} is calling`, {
    body: call.media === 'video' ? 'Video call. Open relaii to answer.' : 'Voice call. Open relaii to answer.',
    tag: `relaii-call-${call.id}`,
    requireInteraction: true,
    silent: true,
  })
  notification.onclick = () => {
    window.focus()
    onOpen()
    notification.close()
  }
  return () => notification.close()
}

export function CallProvider({ children }: { children: ReactNode }): ReactElement {
  const { user, server } = useAuth()
  const callsEnabled = server?.capabilities?.calls === true
  const screenSharingEnabled = callsEnabled && server?.capabilities?.call_screen_sharing === true

  const location = useLocation()
  const { rooms } = useRooms()
  const labels = useRoomLabels()

  const roomName = useCallback(
    (roomId: UUID | undefined): string => {
      const room = rooms.find((entry) => entry.id === roomId)
      return room === undefined ? 'this room' : roomLabel(room, labels)
    },
    [labels, rooms],
  )

  const [incoming, setIncoming] = useState<Call | null>(null)
  const [joinedCall, setJoinedCall] = useState<Call | null>(null)
  const [pending, setPending] = useState<Pending | null>(null)
  const [joining, setJoining] = useState(false)
  const [preferences, setPreferences] = useState<CallPreferences>({ mic: true, camera: true })
  const [startError, setStartError] = useState<string | null>(null)
  // The window is out of the way, the call is not. Only Leave call ends one.
  const [minimised, setMinimised] = useState(false)

  const ringtoneRef = useRef<Ringtone | null>(null)
  const notificationRef = useRef<(() => void) | null>(null)

  const stopRinging = useCallback(() => {
    ringtoneRef.current?.stop()
    ringtoneRef.current = null
    notificationRef.current?.()
    notificationRef.current = null
  }, [])

  // Ring for as long as an invitation is on screen and unanswered, and not
  // a moment longer: answering, declining, the caller giving up and this
  // tab joining something else all stop it.
  useEffect(() => {
    if (incoming === null || joinedCall !== null || pending !== null) {
      stopRinging()
      return
    }
    ringtoneRef.current = startRingtone()
    notificationRef.current = notifyIncoming(incoming, () => undefined)
    return stopRinging
  }, [incoming, joinedCall, pending, stopRinging])

  useEffect(() => {
    if (!callsEnabled) return

    return subscribeCallEvents((event) => {
      if (event.type === 'call.invited') {
        const mine = participantStatus(event.payload.call, user?.id)
        if (mine === 'invited') setIncoming(event.payload.call)
        return
      }

      const updated = event.payload.call

      if (updated.status === 'ended') {
        setIncoming((current) => (current?.id === updated.id ? null : current))
        setJoinedCall((current) => (current?.id === updated.id ? null : current))
        setPending((current) => (current?.kind === 'join' && current.call.id === updated.id ? null : current))
        return
      }

      // The participant list is what tells the session who has left; keep
      // the call we are in current with it.
      setJoinedCall((current) => (current?.id === updated.id ? updated : current))
      setIncoming((current) => (current?.id === updated.id ? updated : current))
    })
  }, [callsEnabled, user?.id])

  const openRoomCall = useCallback((roomId: UUID, label: string) => {
    setStartError(null)
    setPreferences((current) => ({ ...current, mic: true, camera: true }))
    setPending({ kind: 'start', roomId, label })
  }, [])

  const joinExistingCall = useCallback((call: Call, label: string) => {
    setStartError(null)
    setPreferences((current) => ({ ...current, mic: true, camera: call.media === 'video' }))
    setPending({ kind: 'join', call, label })
  }, [])

  const answer = useCallback(
    (camera: boolean) => {
      if (incoming === null) return
      setStartError(null)
      setPreferences((current) => ({ ...current, mic: true, camera: camera && incoming.media === 'video' }))
      setPending({ kind: 'join', call: incoming, label: `Join the ${incoming.media === 'video' ? 'video meeting' : 'call'}` })
      setIncoming(null)
    },
    [incoming],
  )

  const acceptIncoming = useCallback(() => answer(true), [answer])

  // Answering without a camera is a decision people make at the door, not a
  // setting they go looking for once everyone can see them.
  const acceptIncomingAudioOnly = useCallback(() => answer(false), [answer])

  const session = useCallSession(joinedCall, user?.id, joinedCall !== null, preferences)

  // The only place a call is created or entered. Everything before this is a
  // dialog the person can still walk away from.
  const confirmJoin = useCallback(async () => {
    if (pending === null) return
    setJoining(true)
    setStartError(null)
    try {
      // Entering a second call means leaving the first: the server would
      // otherwise keep us as joined in a room we are no longer in.
      if (joinedCall !== null && (pending.kind === 'start' || pending.call.id !== joinedCall.id)) {
        await session.hangUp()
        setJoinedCall(null)
      }
      if (pending.kind === 'start') {
        setJoinedCall(await startCall(pending.roomId, preferences.camera ? 'video' : 'audio'))
      } else {
        setJoinedCall(pending.call)
      }
      setPending(null)
    } catch (caught: unknown) {
      setStartError(caught instanceof Error ? caught.message : 'The call could not be started.')
    } finally {
      setJoining(false)
    }
  }, [joinedCall, pending, preferences.camera, session])

  const declineIncoming = useCallback(async () => {
    if (incoming === null) return
    setIncoming(null)
    await declineCall(incoming.id)
  }, [incoming])

  // Reading another room is not leaving the meeting; the window gets out of
  // the way and the pill keeps it.
  useEffect(() => {
    if (joinedCall !== null) setMinimised(true)
    // The path is the signal, not the call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    if (joinedCall === null) setMinimised(false)
  }, [joinedCall])

  // "Share a screen as I join", acted on once there is a call to share into.
  const sharedOnJoinRef = useRef<UUID | null>(null)
  useEffect(() => {
    if (joinedCall === null || !preferences.shareOnJoin || !session.connected) return
    if (sharedOnJoinRef.current === joinedCall.id) return
    sharedOnJoinRef.current = joinedCall.id
    void session.startShare()
  }, [joinedCall, preferences.shareOnJoin, session])

  const value = useMemo(
    (): CallContextValue => ({
      callsEnabled,
      incoming,
      activeCall: joinedCall,
      openRoomCall,
      joinExistingCall,
      acceptIncoming,
      acceptIncomingAudioOnly,
      declineIncoming,
    }),
    [
      acceptIncoming,
      acceptIncomingAudioOnly,
      callsEnabled,
      declineIncoming,
      incoming,
      joinExistingCall,
      joinedCall,
      openRoomCall,
    ],
  )

  const stage: CallStage | null =
    joinedCall !== null
      ? minimised
        ? null
        : { kind: 'in-call', title: roomName(joinedCall.room_id), call: joinedCall }
      : pending === null
        ? null
        : {
            kind: 'prejoin',
            title: pending.label,
            media: pending.kind === 'join' ? pending.call.media : 'video',
            call: pending.kind === 'join' ? pending.call : null,
            context:
              pending.kind === 'join'
                ? `${roomName(pending.call.room_id)} · started by ${initiatorName(pending.call)}`
                : roomName(pending.roomId),
          }

  const sharingName =
    joinedCall === null || joinedCall.sharing_user_id == null || joinedCall.sharing_user_id === user?.id
      ? null
      : (joinedCall.participants.find((participant) => participant.user?.id === joinedCall.sharing_user_id)?.user
          ?.display_name ?? 'Someone')

  return (
    <CallContext.Provider value={value}>
      {children}
      {callsEnabled ? (
        <>
          {incoming !== null && pending === null ? (
            <IncomingCallPrompt
              call={incoming}
              where={roomName(incoming.room_id)}
              busyIn={joinedCall === null ? null : roomName(joinedCall.room_id)}
              onAccept={acceptIncoming}
              onAcceptAudioOnly={acceptIncomingAudioOnly}
              onDecline={() => void declineIncoming()}
            />
          ) : null}
          {joinedCall !== null && minimised ? (
            <CallPill
              where={roomName(joinedCall.room_id)}
              startedAt={joinedCall.started_at}
              micEnabled={session.micEnabled}
              sharedBy={sharingName}
              onToggleMic={() => session.setMicEnabled(!session.micEnabled)}
              onOpen={() => setMinimised(false)}
              onLeave={() => void session.hangUp().then(() => setJoinedCall(null))}
            />
          ) : null}
          <CallWindow
            stage={stage}
            selfUserId={user?.id}
            session={{ ...session, error: session.error ?? startError }}
            preferences={preferences}
            onPreferencesChange={setPreferences}
            joining={joining}
            screenSharingEnabled={screenSharingEnabled}
            onJoin={() => void confirmJoin()}
            onCancel={() => {
              setPending(null)
              setStartError(null)
            }}
            onMinimise={() => setMinimised(true)}
            onHangUp={() => void session.hangUp().then(() => setJoinedCall(null))}
          />
        </>
      ) : null}
    </CallContext.Provider>
  )
}

/** Load any active call when opening a room. */
export function useRoomCall(roomId: UUID | undefined): Call | null {
  const { callsEnabled, activeCall } = useCalls()
  const [call, setCall] = useState<Call | null>(null)

  useEffect(() => {
    if (!callsEnabled || roomId === undefined) {
      setCall(null)
      return
    }

    let cancelled = false
    void fetchActiveCall(roomId).then((active) => {
      if (!cancelled) setCall(active)
    })

    return () => {
      cancelled = true
    }
  }, [callsEnabled, roomId])

  // A call ending, or the one we are in, reaches this room's button too.
  useEffect(() => {
    return subscribeCallEvents((event) => {
      const updated = event.payload.call
      if (updated.room_id !== roomId) return
      setCall(updated.status === 'ended' ? null : updated)
    })
  }, [roomId])

  return activeCall?.room_id === roomId ? activeCall : call
}

